// Eigenbau: Wiederverwendbarer Kommentarfaden inkl. Reaktionen (Paket 4 / 16)
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/Button";
import { Label } from "@/components/Label";
import { Textarea } from "@/components/Textarea";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { MehrLaden } from "@/components/MehrLaden";
import { fehlerMeldung, useCursorListe, useIch } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";

const EMOJIS = ["👍", "👀", "🎉", "❤️"] as const;

export function KommentareFaden({
  projektId,
  betrifftArt,
  betrifftId,
  markeId,
  kannSchreiben,
  testId = "kommentare-faden",
}: {
  projektId: string;
  betrifftArt: "aufgabe" | "seite" | "marke";
  betrifftId: string;
  markeId?: string | null;
  kannSchreiben: boolean;
  testId?: string;
}) {
  const client = useQueryClient();
  const ich = useIch();
  const [text, setzeText] = useState("");
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);
  const [bearbeitung, setzeBearbeitung] = useState<{
    kommentarId: string;
    inhalt: string;
  } | null>(null);
  const [loeschBestaetigung, setzeLoeschBestaetigung] = useState<string | null>(
    null,
  );
  const schluessel = [
    "cubic",
    "kommentare",
    projektId,
    betrifftArt,
    betrifftId,
    markeId ?? null,
  ] as const;

  const kommentare = useCursorListe({
    queryKey: schluessel,
    lade: (cursor) =>
      api.kommentare.liste.query({
        projektId,
        betrifftArt,
        betrifftId,
        cursor,
        grenze: 50,
      }),
    enabled: Boolean(projektId && betrifftId),
  });

  const schreiben = useMutation({
    mutationFn: () =>
      api.kommentare.schreiben.mutate({
        projektId,
        betrifftArt,
        betrifftId,
        inhalt: text.trim(),
        markeId: markeId ?? undefined,
      }),
    onSuccess: async () => {
      setzeText("");
      setzeMeldung(null);
      await client.invalidateQueries({ queryKey: ["cubic", "kommentare"] });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const reaktion = useMutation({
    mutationFn: (eingabe: {
      kommentarId: string;
      emoji: string;
      gesetzt: boolean;
    }) => api.kommentare.reaktion.mutate(eingabe),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["cubic", "kommentare"] });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const aendern = useMutation({
    mutationFn: (eingabe: {
      kommentarId: string;
      inhalt: string;
      erwarteteRevision: Date;
    }) => api.kommentare.aendern.mutate(eingabe),
    onSuccess: async () => {
      setzeBearbeitung(null);
      setzeMeldung(null);
      await client.invalidateQueries({ queryKey: ["cubic", "kommentare"] });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const loeschen = useMutation({
    mutationFn: (eingabe: {
      kommentarId: string;
      erwarteteRevision: Date;
    }) => api.kommentare.loeschen.mutate(eingabe),
    onSuccess: async () => {
      setzeLoeschBestaetigung(null);
      setzeBearbeitung(null);
      setzeMeldung(null);
      await client.invalidateQueries({ queryKey: ["cubic", "kommentare"] });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const gefiltert = kommentare.eintraege.filter((k) =>
    markeId ? k.markeId === markeId : true,
  );

  return (
    <section className="space-y-3" data-testid={testId}>
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
        Kommentare
        {markeId ? " an Marke" : ""}
      </h2>
      {meldung ? <Fehlerzeile meldung={meldung} /> : null}
      {kommentare.isLoading ? (
        <p className="text-sm text-gray-500">Lade Kommentare …</p>
      ) : kommentare.isError ? (
        <Fehlerzeile
          meldung={fehlerMeldung(kommentare.error)}
          onErneut={() => void kommentare.refetch()}
        />
      ) : gefiltert.length === 0 ? (
        <p className="text-sm text-gray-500">Noch keine Kommentare.</p>
      ) : (
        <ul className="space-y-3">
          {gefiltert.map((k) => {
            const reaks = k.reaktionen ?? [];
            const istAutor = k.angelegtVon === ich.data?.personId;
            const istGeloescht = Boolean(k.geloeschtAm);
            const wirdBearbeitet = bearbeitung?.kommentarId === k.id;
            const zaehlung = EMOJIS.map((emoji) => ({
              emoji,
              anzahl: reaks.filter((r) => r.emoji === emoji).length,
            })).filter((x) => x.anzahl > 0);
            return (
              <li
                key={k.id}
                className="rounded-md border border-gray-200 p-3 text-sm dark:border-gray-800"
              >
                {wirdBearbeitet ? (
                  <div className="space-y-2">
                    <Textarea
                      aria-label="Kommentar bearbeiten"
                      rows={3}
                      value={bearbeitung.inhalt}
                      maxLength={20_000}
                      onChange={(ereignis) =>
                        setzeBearbeitung({
                          kommentarId: k.id,
                          inhalt: ereignis.target.value,
                        })
                      }
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() =>
                          aendern.mutate({
                            kommentarId: k.id,
                            inhalt: bearbeitung.inhalt.trim(),
                            erwarteteRevision: new Date(k.geaendertAm),
                          })
                        }
                        disabled={
                          aendern.isPending ||
                          bearbeitung.inhalt.trim().length === 0 ||
                          bearbeitung.inhalt.trim() === k.inhalt
                        }
                      >
                        Speichern
                      </Button>
                      <button
                        type="button"
                        className="px-2 text-xs text-gray-500 underline underline-offset-2"
                        onClick={() => setzeBearbeitung(null)}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <p
                    className={
                      istGeloescht
                        ? "italic text-gray-500"
                        : "whitespace-pre-wrap text-gray-900 dark:text-gray-50"
                    }
                  >
                    {istGeloescht ? "Dieser Kommentar wurde gelöscht." : k.inhalt}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {k.autorName ?? "Gelöschte Person"} ·{" "}
                  {new Date(k.angelegtAm).toLocaleString("de-DE")}
                  {!istGeloescht &&
                  new Date(k.geaendertAm).getTime() >
                    new Date(k.angelegtAm).getTime()
                    ? " · bearbeitet"
                    : ""}
                </p>
                {istAutor && kannSchreiben && !istGeloescht && !wirdBearbeitet ? (
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <button
                      type="button"
                      className="text-gray-600 underline underline-offset-2 dark:text-gray-400"
                      onClick={() => {
                        setzeBearbeitung({ kommentarId: k.id, inhalt: k.inhalt });
                        setzeLoeschBestaetigung(null);
                      }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      className="text-red-700 underline underline-offset-2 dark:text-red-400"
                      disabled={loeschen.isPending}
                      onClick={() => {
                        if (loeschBestaetigung !== k.id) {
                          setzeLoeschBestaetigung(k.id);
                          return;
                        }
                        loeschen.mutate({
                          kommentarId: k.id,
                          erwarteteRevision: new Date(k.geaendertAm),
                        });
                      }}
                    >
                      {loeschBestaetigung === k.id
                        ? "Löschen bestätigen"
                        : "Löschen"}
                    </button>
                  </div>
                ) : null}
                {kannSchreiben && !istGeloescht ? (
                  <div
                    className="mt-2 flex flex-wrap items-center gap-1"
                    role="group"
                    aria-label="Reaktionen"
                  >
                    {EMOJIS.map((emoji) => {
                      const n = zaehlung.find((z) => z.emoji === emoji)?.anzahl;
                      const vonMir = reaks.some(
                        (eintrag) =>
                          eintrag.emoji === emoji &&
                          eintrag.personId === ich.data?.personId,
                      );
                      return (
                        <button
                          key={emoji}
                          type="button"
                          className={`rounded px-1.5 py-0.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                            vonMir ? "bg-gray-100 ring-1 ring-gray-300 dark:bg-gray-800" : ""
                          }`}
                          aria-label={`${vonMir ? "Reaktion entfernen" : "Reagieren mit"} ${emoji}`}
                          aria-pressed={vonMir}
                          disabled={reaktion.isPending}
                          onClick={() =>
                            reaktion.mutate({
                              kommentarId: k.id,
                              emoji,
                              gesetzt: !vonMir,
                            })
                          }
                        >
                          {emoji}
                          {n ? ` ${n}` : ""}
                        </button>
                      );
                    })}
                  </div>
                ) : zaehlung.length > 0 ? (
                  <p className="mt-2 text-xs text-gray-500">
                    {zaehlung.map((z) => `${z.emoji} ${z.anzahl}`).join(" · ")}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <MehrLaden
        hatMehr={kommentare.hasNextPage}
        laedt={kommentare.isFetchingNextPage}
        laden={() => void kommentare.fetchNextPage()}
        beschriftung="Ältere Kommentare laden"
      />
      {kannSchreiben ? (
        <div className="space-y-2">
          <Label htmlFor={`${testId}-neu`}>Neuer Kommentar</Label>
          <Textarea
            id={`${testId}-neu`}
            rows={3}
            value={text}
            maxLength={20_000}
            onChange={(e) => {
              setzeText(e.target.value);
              setzeMeldung(null);
            }}
          />
          <Button
            onClick={() => schreiben.mutate()}
            disabled={schreiben.isPending || text.trim().length === 0}
            data-testid={`${testId}-schreiben`}
          >
            Kommentieren
          </Button>
        </div>
      ) : null}
    </section>
  );
}
